import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  MedicationDataTypeConfig,
  ProjectDisplayConfig
} from "../resources/project/ProjectShow/ProjectDisplayConfigurationDialog";
import { useCategoryMetadata } from "src/providers/CategoryMetadataContext";
import { useDataProvider, useGetIdentity, useRecordContext } from "react-admin";
import {
  getDataAvailabilityConfiguration,
  getMobilityStreamParams,
  getSleepStageStreamParams,
  getTapTestStreamParams,
  getTremorStreamParams,
  getStreamIdsForCategories
} from "src/providers/streams/runeStreamsUtils";
import {
  deriveTimestampsFromDate,
  getBrowserTimezone
} from "src/utils/dateTimeUtils";
import { get } from "lodash";

import { COLORS } from "../common/colors";
import { useMediaQuery } from "@mui/material";
import { useFlags } from "../../clients/launchDarkly";

// Define the types for your data and events
export type DataType = {
  date: string;
  patientId: string;
  tremor_dyskinesia?: number;
  mobility?: number;
  sleep?: number;
  /** Medication counts keyed by classification enum (e.g., "rytary") */
  medication?: Record<string, number>;
  /** Maps classification enum to display note (e.g., "rytary" → "Rytary (2 pills x 95 mg)") */
  medicationDisplayNames?: Record<string, string>;
  tap_test?: number;
  bowel_movement?: number;
};

const getColor = (
  value: number,
  threshold: number,
  date: string,
  onboardedDate: Date | null,
  startDate: Date | null,
  endDate: Date | null,
  key: keyof DataType,
  projectDisplayConfig: ProjectDisplayConfig
): string => {
  const colorMode: "YELLOW_RED" | "CLINICIAN_CALENDAR" = projectDisplayConfig
    .visualization.highlightMissingData
    ? "YELLOW_RED"
    : "CLINICIAN_CALENDAR";

  switch (colorMode) {
    case "YELLOW_RED":
      if (startDate && new Date(date) < startDate) {
        return COLORS.GREY_400;
      }
      if (endDate && new Date(date) > endDate) {
        return COLORS.GREY_400;
      }
      if (value > threshold || (threshold === undefined && value > 0))
        return COLORS.PRIMARY_300;
      if (value > 0) return COLORS.YELLOW_200;
      return COLORS.RED_200;
    case "CLINICIAN_CALENDAR":
      if (value > 0) {
        if (startDate && new Date(date) < startDate) {
          return COLORS.GREY_200;
        }
        if (endDate && new Date(date) > endDate) {
          return COLORS.GREY_200;
        }
        if (key === "tremor_dyskinesia") {
          return COLORS.PRIMARY_1200;
        }
        if (key === "mobility") {
          return COLORS.PRIMARY_700;
        }
        if (key === "sleep") {
          return COLORS.PRIMARY_400;
        }
        if (
          key === "medication" ||
          key === "tap_test" ||
          key === "bowel_movement"
        ) {
          return COLORS.PRIMARY_300;
        }
        console.log(`the datatype ${key} is not handled`);
        return COLORS.RED_200;
      }
      return COLORS.GREY_200;
  }
};

const isMonday = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date.getDay() === 1;
};

const isFirstOfMonth = (dateString: string): boolean => {
  const date = new Date(dateString);
  return date.getDate() === 1;
};

const getMonthLabelPositions = (data: DataType[]) => {
  const labels: { text: string; position: number }[] = [];
  let lastMonth = "";
  let firstMonthDays = 0;

  data.forEach((entry, index) => {
    const currentDate = new Date(entry.date);
    const month = currentDate.toLocaleString("default", { month: "short" });

    if (month !== lastMonth) {
      if (index === 0) {
        // This is the first month: we calculate the number of days in this month.
        // If less than 8 days, we don't display the month (fix the overlapping months issue)
        const nextMonth = new Date(currentDate);
        nextMonth.setMonth(currentDate.getMonth() + 1);
        const lastDayOfMonth = new Date(
          nextMonth.getFullYear(),
          nextMonth.getMonth(),
          0
        );
        firstMonthDays = lastDayOfMonth.getDate() - currentDate.getDate() + 1;
      }

      if (labels.length > 0 || firstMonthDays >= 8) {
        labels.push({ text: month, position: index });
      }

      lastMonth = month;
    }
  });

  return labels;
};

const isMedRow = (key: string): boolean => key.startsWith("medication:");
const isMedGroupRow = (key: string): boolean =>
  key.startsWith("medication-group:");
const getMedEnum = (key: string): string => key.replace("medication:", "");
const getMedGroupTag = (key: string): string =>
  key.replace("medication-group:", "");

/** Get the numeric cell value for a swimlane key from a data entry */
const getCellValue = (
  entry: DataType,
  key: string,
  config?: ProjectDisplayConfig,
  liveTagGroups?: Map<string, string[]>
): number => {
  if (key === "medication") {
    return entry.medication
      ? Object.values(entry.medication).reduce((a, b) => a + b, 0)
      : 0;
  }
  if (isMedRow(key)) {
    const medEnum = getMedEnum(key);
    return entry.medication?.[medEnum] || 0;
  }
  if (isMedGroupRow(key) && config) {
    const tagName = getMedGroupTag(key);
    const medConfig = config.dataTypes.medication as MedicationDataTypeConfig;
    // Prefer live metadata enums over the snapshot stored in config
    const medicationEnums =
      liveTagGroups?.get(tagName) ??
      medConfig.selectedGroups?.find((g) => g.tagName === tagName)
        ?.medicationEnums;
    if (!medicationEnums || !entry.medication) return 0;
    return medicationEnums.reduce(
      (sum, medEnum) => sum + (entry.medication?.[medEnum] || 0),
      0
    );
  }
  return (entry[key as keyof DataType] as number) || 0;
};

const getLegendText = (key: keyof DataType): string => {
  const legends: { [key in keyof DataType]?: string } = {
    tremor_dyskinesia: "T&D",
    mobility: "Mobility",
    sleep: "Sleep",
    medication: "Meds",
    tap_test: "Tap Test",
    bowel_movement: "BM"
  };
  return legends[key] || key;
};

/** Get legend text for dynamic keys including medication:enum and medication-group: rows */
const getDynamicLegendText = (
  key: string,
  config: ProjectDisplayConfig
): string => {
  if (isMedRow(key)) {
    const medClassification = getMedEnum(key);
    const medConfig = config.dataTypes.medication as MedicationDataTypeConfig;
    const selected = medConfig.selectedMedications?.find(
      (m) => m.classification === medClassification
    );
    return selected?.name || medClassification;
  }
  if (isMedGroupRow(key)) {
    const tagName = getMedGroupTag(key);
    const medConfig = config.dataTypes.medication as MedicationDataTypeConfig;
    const group = medConfig.selectedGroups?.find((g) => g.tagName === tagName);
    return group?.displayName || tagName;
  }
  return getLegendText(key as keyof DataType);
};

const getLegendTooltip = (key: keyof DataType): string => {
  const tooltips: { [key in keyof DataType]?: string } = {
    tremor_dyskinesia: "Tremor & Dyskinesia is collected from the Apple Watch",
    mobility:
      "Mobility is collected from the iPhone when the user is actively moving",
    sleep: "Sleep is collected from the Apple Watch",
    medication: "Medications are manually reported by the patient",
    tap_test: "Tap tests are manually performed by the patient",
    bowel_movement: "Bowel movements are manually reported by the patient"
  };
  return tooltips[key] || key;
};

const isActive = (
  entry: DataType,
  startDate: Date | null,
  endDate: Date | null,
  enabled: boolean
) => {
  const date = new Date(entry.date).getTime();
  // tooltips should only be shown for dates within the project start and end dates
  // or if the project start and end dates are not set
  let afterProjectStart = true;
  let beforeProjectEnd = true;
  if (startDate && enabled) {
    afterProjectStart = date >= startDate.getTime();
  }
  if (endDate && enabled) {
    beforeProjectEnd = date <= endDate.getTime();
  }

  return afterProjectStart && beforeProjectEnd;
};

const getTooltipText = (
  entry: DataType,
  key: keyof DataType,
  userCanSeePII: boolean
): string => {
  if (key === "medication" && entry.medication) {
    if (!userCanSeePII) {
      // If the user cannot see PII, we display the total number of medications
      const totalMedicationCount = Object.values(entry.medication).reduce(
        (sum, count) => sum + count,
        0
      );
      return `<b>${new Date(
        entry.date
      ).toLocaleDateString()}</b><br />Total Medication: ${totalMedicationCount}`;
    } else {
      // If the user can see PII, we display the details of the medications
      // Use display names map if available, fall back to enum key
      const displayNames = entry.medicationDisplayNames || {};
      const medicationDetails = Object.entries(entry.medication)
        .map(
          ([medEnum, count]) => `${displayNames[medEnum] || medEnum}: ${count}`
        )
        .join("<br />");
      return `<b>${new Date(
        entry.date
      ).toLocaleDateString()}</b><br />${medicationDetails}`;
    }
  }

  if (key === "tap_test") {
    const count = (entry[key] as number) || 0;
    return `<b>${new Date(
      entry.date
    ).toLocaleDateString()}</b><br />Tap Test: ${
      count > 0 ? `${count} completed` : "None"
    }`;
  }

  if (key === "bowel_movement") {
    const count = (entry[key] as number) || 0;
    return `<b>${new Date(
      entry.date
    ).toLocaleDateString()}</b><br />Bowel Movement: ${
      count > 0 ? `${count} recorded` : "None"
    }`;
  }

  // If the key is not medication, we display the value of the key (duration in hours)
  return `<b>${new Date(
    entry.date
  ).toLocaleDateString()}</b><br />${getLegendText(key)}: ${
    isNaN(entry[key] as number) || entry[key] === 0
      ? "None"
      : `${Math.round(entry[key] as number)} hours`
  }`;
};

/** Tooltip for per-medication and medication-group swimlane rows */
const getMedRowTooltipText = (
  entry: DataType,
  key: string,
  userCanSeePII: boolean,
  config: ProjectDisplayConfig,
  liveTagGroups?: Map<string, string[]>
): string => {
  const displayName = getDynamicLegendText(key, config);
  const count = getCellValue(entry, key, config, liveTagGroups);

  if (isMedGroupRow(key) && userCanSeePII && entry.medication) {
    const tagName = getMedGroupTag(key);
    const medConfig = config.dataTypes.medication as MedicationDataTypeConfig;
    const medicationEnums =
      liveTagGroups?.get(tagName) ??
      medConfig.selectedGroups?.find((g) => g.tagName === tagName)
        ?.medicationEnums;
    if (medicationEnums) {
      const displayNames = entry.medicationDisplayNames || {};
      const details = medicationEnums
        .filter((e) => (entry.medication?.[e] || 0) > 0)
        .map((e) => `${displayNames[e] || e}: ${entry.medication?.[e]}`)
        .join("<br />");
      if (details) {
        return `<b>${new Date(
          entry.date
        ).toLocaleDateString()}</b><br />${displayName}<br />${details}`;
      }
    }
  }

  if (isMedRow(key) && userCanSeePII) {
    const medEnum = getMedEnum(key);
    const displayNote = entry.medicationDisplayNames?.[medEnum] || displayName;
    return `<b>${new Date(
      entry.date
    ).toLocaleDateString()}</b><br />${displayNote}: ${count}`;
  }

  return `<b>${new Date(
    entry.date
  ).toLocaleDateString()}</b><br />${displayName}: ${count}`;
};

const hasCustomMedication = (
  displayNames: Record<string, string> | undefined
): boolean => {
  if (!displayNames) return false;
  return Object.values(displayNames).some(
    (name) => name.includes("(C)") || name.includes("Custom Event")
  );
};

const DataAvailabilityHeatmapCalendarField = (props: {
  projectDisplayConfig: ProjectDisplayConfig;
  dataAvailability: string;
  projectStart: Date;
}) => {
  const { projectDisplayConfig, dataAvailability } = props;
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const { data: user } = useGetIdentity();
  const { tagGroupsByName } = useCategoryMetadata();

  const isAdmin =
    user?.defaultMembership?.role.admin ||
    user?.defaultMembership?.role.superAdmin;
  const canSeePII = user?.defaultMembership?.role.canSeePHI;

  const patientId = get(record, "id");
  const { patientStartEndDate } = useFlags();
  const projectStart =
    patientStartEndDate && get(record, "projectStartTime")
      ? new Date(get(record, "projectStartTime") * 1000)
      : null;
  const projectEnd =
    patientStartEndDate && get(record, "projectEndTime")
      ? new Date(get(record, "projectEndTime") * 1000)
      : null;
  const patientOnboardedDate = get(record, "createdAt")
    ? new Date(get(record, "createdAt") * 1000)
    : null;
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [availabilityData, setAvailabilityData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Build dynamic swimlane keys, expanding medication into per-medication rows if configured
  const buildSwimLaneKeys = (): string[] => {
    const keys: string[] = [];
    const dt = projectDisplayConfig.dataTypes;

    if (dt.tremor_dyskinesia?.enabled) keys.push("tremor_dyskinesia");
    if (dt.mobility?.enabled) keys.push("mobility");
    if (dt.sleep?.enabled) keys.push("sleep");

    if (dt.medication?.enabled) {
      const medConfig = dt.medication as MedicationDataTypeConfig;
      const hasIndividual = medConfig.selectedMedications?.length;
      const hasGroups = medConfig.selectedGroups?.length;

      if (medConfig.showAll) {
        keys.push("medication");
      }
      if (hasIndividual) {
        for (const med of medConfig.selectedMedications!) {
          keys.push(`medication:${med.classification}`);
        }
      }
      if (hasGroups) {
        for (const group of medConfig.selectedGroups!) {
          keys.push(`medication-group:${group.tagName}`);
        }
      }
      if (!medConfig.showAll && !hasIndividual && !hasGroups) {
        keys.push("medication");
      }
    }

    if (dt.tap_test?.enabled) keys.push("tap_test");
    if (dt.bowel_movement?.enabled) keys.push("bowel_movement");

    return keys;
  };

  const orderedDataKeys = buildSwimLaneKeys();

  const monthLabels = getMonthLabelPositions(availabilityData);
  const handleMouseEnter = (
    e: React.MouseEvent,
    key: string,
    entry: DataType
  ) => {
    if (
      svgRef.current &&
      isActive(entry, projectStart, projectEnd, patientStartEndDate)
    ) {
      const rect = svgRef.current.getBoundingClientRect();
      const tooltipText =
        isMedRow(key) || isMedGroupRow(key)
          ? getMedRowTooltipText(
              entry,
              key,
              canSeePII,
              projectDisplayConfig,
              tagGroupsByName
            )
          : getTooltipText(entry, key as keyof DataType, canSeePII);
      setTooltip({
        text: tooltipText,
        x: e.clientX - rect.left + 10,
        y: e.clientY - rect.top + 10
      });
    }
  };

  const handleCellClick = (key: string, entry: DataType) => {
    const clickedDate = new Date(entry.date);
    const oneDayBefore = new Date(clickedDate);
    oneDayBefore.setDate(clickedDate.getDate() - 1);
    const oneDayAfter = new Date(clickedDate);
    oneDayAfter.setDate(clickedDate.getDate() + 1);

    const formatDate = (date: Date) => date.toISOString().split("T")[0];

    const date = formatDate(clickedDate);
    const startDate = formatDate(oneDayBefore);
    const endDate = formatDate(oneDayAfter);

    // We determine the portal url based on the environment (specifically the graphQL endpoint from the config)
    const carrotGraphUrl =
      (window as any).Rune?.Carrot?.config?.carrotGraph?.host || "";
    const strivePdPortalUrl = carrotGraphUrl.replace("graph", "app");

    if (
      key === "medication" ||
      isMedRow(key) ||
      isMedGroupRow(key) ||
      key === "bowel_movement"
    ) {
      window.open(
        `https://${strivePdPortalUrl}/patients/${entry.patientId}/log?startTime=${startDate}&endTime=${endDate}`,
        "_blank"
      );
    } else if (key === "tremor_dyskinesia") {
      window.open(
        `/#/Patient/${entry.patientId}/show/3?date=${date}`,
        "_blank"
      );
    } else {
      window.open(
        `https://${strivePdPortalUrl}/patients/${entry.patientId}/daily?startTime=${startDate}&endTime=${endDate}`,
        "_blank"
      );
    }
  };

  const cellSize = 15; // Size of each cell in the grid
  const padding = 5;
  const hasExpandedMedRows = orderedDataKeys.some(
    (k) => isMedRow(k) || isMedGroupRow(k)
  );
  const HORIZONTAL_OFFSET = hasExpandedMedRows ? 150 : 110;

  const [legendTooltip, setLegendTooltip] = useState<{
    text: string;
    key: string;
  } | null>(null);

  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleLegendMouseEnter = useCallback(
    (key: string) => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      let tooltipText: string;
      if (isMedRow(key) || isMedGroupRow(key)) {
        const fullName = getDynamicLegendText(key, projectDisplayConfig);
        tooltipText = `${fullName}: Manually reported by the patient`;
      } else {
        tooltipText = getLegendTooltip(key as keyof DataType);
      }
      setLegendTooltip({
        text: tooltipText,
        key: key
      });
    },
    [projectDisplayConfig]
  );

  const handleLegendMouseLeave = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    tooltipTimeoutRef.current = setTimeout(() => {
      setLegendTooltip(null);
    }, 300); // Delay before hiding the tooltip
  }, []);

  const isSmallScreen = useMediaQuery("(max-width:1500px)");

  useEffect(() => {
    const enabledCategories = (
      Object.keys(
        projectDisplayConfig["dataTypes"]
      ) as (keyof ProjectDisplayConfig["dataTypes"])[]
    ).filter(
      (key) =>
        projectDisplayConfig["dataTypes"][
          key as keyof ProjectDisplayConfig["dataTypes"]
        ].enabled
    );

    setAvailabilityData([]);
    setLoading(true);

    const fetchStreamData = async () => {
      const streamParamsFunctions = {
        tremor_dyskinesia: getTremorStreamParams,
        mobility: getMobilityStreamParams,
        sleep: getSleepStageStreamParams,
        tap_test: getTapTestStreamParams
      };

      let { startDate } = getDataAvailabilityConfiguration(
        props.dataAvailability,
        props.projectStart
      );
      const { aggregateWindowResolution } = getDataAvailabilityConfiguration(
        props.dataAvailability,
        props.projectStart
      );

      // Adjust start date based on screen size
      const now = new Date();
      startDate = isSmallScreen
        ? new Date(now.setMonth(now.getMonth() - 2))
        : new Date(now.setDate(now.getDate() - 90));

      const browserTimezone = getBrowserTimezone();
      const { startDateTimestamp, endDateTimestamp } =
        deriveTimestampsFromDate(startDate);

      const startTime = startDateTimestamp;
      const endTime = endDateTimestamp;

      const desiredCategories: (keyof ProjectDisplayConfig["dataTypes"])[] = [
        "tremor_dyskinesia",
        "mobility",
        "sleep",
        "tap_test"
      ];

      const streamIdsResults = await getStreamIdsForCategories({
        dataProvider,
        desiredCategories,
        enabledCategories,
        patientId,
        streamParamsFunctions
      });

      const streamsDataPromises: {
        category: string;
        promise: Promise<any>;
      }[] = [];

      let tapTestStreamId: string | null = null;

      // Retrieve data for each Stream ID using getAggregateWindow
      for (const streamIdsByCategory of streamIdsResults) {
        if (
          streamIdsByCategory["category"] === "tremor_dyskinesia" ||
          streamIdsByCategory["category"] === "mobility" ||
          streamIdsByCategory["category"] === "sleep"
        ) {
          // We check that there is only one stream ID
          if (streamIdsByCategory["streamIds"]["data"].length > 1) {
            console.log("streamIdsByCategory", streamIdsByCategory);
            console.error(
              `Expected one stream ID MAXIMUM for ${streamIdsByCategory["category"]}`
            );
            continue;
          }
          if (streamIdsByCategory["streamIds"]["data"].length === 0) {
            continue;
          }
          const promise = dataProvider.getStreamAggregateWindow(
            streamIdsByCategory["streamIds"]["data"][0]["id"],
            {
              start_time: JSON.stringify(startTime),
              end_time: endTime,
              resolution: JSON.stringify(aggregateWindowResolution),
              aggregate_function: "sum",
              // TODO: SW-3563: Timezone are currently not handled correctly.
              // It should be taking into account the timezone of the user instead of being the browser timezone.
              // A full revamp/revisit/refactor of the timezone management is expected
              timezone_name: browserTimezone
            }
          );
          streamsDataPromises.push({
            category: streamIdsByCategory["category"],
            promise
          });
        } else if (streamIdsByCategory["category"] === "tap_test") {
          if (streamIdsByCategory["streamIds"]["data"].length === 1) {
            tapTestStreamId = streamIdsByCategory["streamIds"]["data"][0]["id"];
          }
        }
      }

      const streamsDataResults = await Promise.all(
        streamsDataPromises.map((p) => p.promise)
      );

      // Initialize availabilityData based on the retrieved data from streams
      const nanoSecondsPerHour = 1e9 * 60 * 60;
      const newAvailabilityData = streamsDataResults
        .map((streamData, index) => {
          const category = streamsDataPromises[index].category;

          const { time, duration_sum } = streamData.json.data;

          return time.map((timestamp: number, index: number) => ({
            patientId: patientId,
            date: new Date(timestamp * 1000).toISOString().split("T")[0], // Convert UNIX timestamp to ISO date string
            [category]: duration_sum[index] / nanoSecondsPerHour
          }));
        })
        .flat();

      // Tap test streams are non-numeric (span type) so they can't use aggregate_window.
      // Fetch raw spans and count per day using start_time.
      if (tapTestStreamId) {
        const tapTestData = await dataProvider.getStream(tapTestStreamId, {
          startTime: JSON.stringify(startTime),
          endTime: endTime,
          format: "json"
        });
        const tapStartTimes: number[] =
          tapTestData?.json?.data?.start_time ?? [];
        tapStartTimes.forEach((timestamp: number) => {
          const date = new Date(timestamp * 1000).toISOString().split("T")[0];
          const existing = newAvailabilityData.find((e) => e.date === date);
          if (existing) {
            existing.tap_test = (existing.tap_test || 0) + 1;
          } else {
            newAvailabilityData.push({ patientId, date, tap_test: 1 });
          }
        });
      }

      // Fetch patient events if medication or bowel_movement is enabled
      const needsEvents =
        projectDisplayConfig["dataTypes"]["medication"]?.enabled ||
        projectDisplayConfig["dataTypes"]["bowel_movement"]?.enabled;

      if (needsEvents) {
        const includeFilters = [];
        if (projectDisplayConfig["dataTypes"]["medication"]?.enabled) {
          includeFilters.push({
            namespace: "patient",
            category: "medication",
            enum: "*"
          });
        }
        if (projectDisplayConfig["dataTypes"]["bowel_movement"]?.enabled) {
          includeFilters.push({
            namespace: "patient",
            category: "bodily-function",
            enum: "bowel-movement"
          });
        }

        const eventData = await dataProvider.getManyReference("PatientEvent", {
          target: "patient",
          id: patientId,
          pagination: {
            page: 1,
            perPage: 100
          },
          sort: {
            field: "createdAt",
            order: "DESC"
          },
          filter: {},
          meta: {
            enablePiiToggle: canSeePII,
            includeFilters
          }
        });

        const filteredEvents = eventData.data.filter(
          (event) => event.duration.startTime > startDateTimestamp
        );

        // Process medication events
        if (projectDisplayConfig["dataTypes"]["medication"]?.enabled) {
          const medicationEvents = filteredEvents.filter(
            (event) => event.classification?.category === "medication"
          );

          medicationEvents.forEach((medicationEvent) => {
            const date = new Date(medicationEvent.duration.startTime * 1000)
              .toISOString()
              .split("T")[0];

            // Use classification enum as the stable key (e.g., "rytary")
            const medEnum =
              medicationEvent.medication ||
              medicationEvent.classification?.enum ||
              "unknown";

            // Build display name for tooltips
            let medicationDisplayName = medicationEvent.displayNote;
            if (
              medicationEvent.displayName == "Custom Event" &&
              medicationEvent.customDetail?.displayName
            ) {
              medicationDisplayName = `${medicationEvent.customDetail.displayName} (C)`;
            }
            if (medicationEvent.method == "autolog")
              medicationDisplayName = `${medicationDisplayName} (A)`;

            const existingEntry = newAvailabilityData.find(
              (entry) => entry.date === date
            );
            if (existingEntry) {
              if (!existingEntry.medication) {
                existingEntry.medication = {};
              }
              if (!existingEntry.medicationDisplayNames) {
                existingEntry.medicationDisplayNames = {};
              }
              medEnum in existingEntry.medication
                ? (existingEntry.medication[medEnum] += 1)
                : (existingEntry.medication[medEnum] = 1);
              // Store the latest display name for this enum
              existingEntry.medicationDisplayNames[medEnum] =
                medicationDisplayName;
            } else {
              newAvailabilityData.push({
                patientId: patientId,
                date: date,
                medication: { [medEnum]: 1 },
                medicationDisplayNames: {
                  [medEnum]: medicationDisplayName
                }
              });
            }
          });
        }

        // Process bowel movement events
        if (projectDisplayConfig["dataTypes"]["bowel_movement"]?.enabled) {
          const bmEvents = filteredEvents.filter(
            (event) =>
              event.classification?.category === "bodily-function" &&
              event.classification?.enum === "bowel-movement"
          );

          bmEvents.forEach((bmEvent) => {
            const date = new Date(bmEvent.duration.startTime * 1000)
              .toISOString()
              .split("T")[0];

            const existingEntry = newAvailabilityData.find(
              (entry) => entry.date === date
            );
            if (existingEntry) {
              existingEntry.bowel_movement =
                (existingEntry.bowel_movement || 0) + 1;
            } else {
              newAvailabilityData.push({
                patientId: patientId,
                date: date,
                bowel_movement: 1
              });
            }
          });
        }
      }

      // Aggregate data by date
      const dataByDate = newAvailabilityData.reduce((acc, entry) => {
        const { date, patientId, ...rest } = entry;
        if (!acc[date]) {
          acc[date] = { date, patientId }; // Initialize with date and patientId
        }
        // Merge entries by category for the same date
        Object.assign(acc[date], rest);
        return acc;
      }, {});

      // Convert the object back to array
      const aggregatedData: DataType[] = Object.values(dataByDate);

      // Sort the array by date
      aggregatedData.sort(
        (a: DataType, b: DataType) =>
          new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      setAvailabilityData(aggregatedData);
      setLoading(false);
    };

    fetchStreamData();
  }, [
    dataProvider,
    projectDisplayConfig,
    patientId,
    dataAvailability,
    isSmallScreen
  ]);

  if (loading && availabilityData.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <svg
          width="100%"
          height={orderedDataKeys.length * (cellSize + padding) + 20}
        >
          {orderedDataKeys.map((key, rowIndex) => (
            <React.Fragment key={key}>
              <text
                x="50"
                y={rowIndex * (cellSize + padding) + 32}
                fontSize="12"
                fill="#999"
              >
                {getDynamicLegendText(key, projectDisplayConfig)}
              </text>
              <rect
                x={HORIZONTAL_OFFSET}
                y={rowIndex * (cellSize + padding) + 20}
                width={90 * cellSize}
                height={cellSize}
                rx={3}
                fill={COLORS.GREY_200}
              >
                <animate
                  attributeName="opacity"
                  values="0.4;1;0.4"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </rect>
            </React.Fragment>
          ))}
        </svg>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <svg
        ref={svgRef}
        width={availabilityData.length * cellSize + HORIZONTAL_OFFSET + 20}
        height={orderedDataKeys.length * (cellSize + padding) + 20}
      >
        {monthLabels.map((label, index) => (
          <text
            key={`${patientId}-month-${label.text}-${index}`}
            x={label.position * cellSize + HORIZONTAL_OFFSET}
            y={15}
            fill="#666"
          >
            {label.text}
          </text>
        ))}
        {orderedDataKeys.map((key, rowIndex) => (
          <React.Fragment key={key + rowIndex}>
            <g
              transform={`translate(50, ${
                rowIndex * (cellSize + padding) + 17
              })`}
            >
              <g
                onMouseEnter={() => handleLegendMouseEnter(key)}
                onMouseLeave={handleLegendMouseLeave}
                style={{ cursor: "help" }}
              >
                <rect
                  x="-5"
                  y="0"
                  width={hasExpandedMedRows ? 100 : 60}
                  height="20"
                  fill="transparent"
                />
                <text x="0" y="15" fontSize="12" fill="#000">
                  {(() => {
                    const label = getDynamicLegendText(
                      key,
                      projectDisplayConfig
                    );
                    const maxLen = 14;
                    return label.length > maxLen
                      ? label.slice(0, maxLen - 1) + "…"
                      : label;
                  })()}
                </text>
              </g>
            </g>
            {availabilityData.map((entry, colIndex) => (
              <g
                key={`${patientId}-${key}-${entry.date}-${rowIndex}-${colIndex}`}
              >
                <rect
                  x={colIndex * cellSize + HORIZONTAL_OFFSET}
                  y={rowIndex * (cellSize + padding) + 20}
                  width={cellSize}
                  height={cellSize}
                  fill={getColor(
                    getCellValue(
                      entry,
                      key,
                      projectDisplayConfig,
                      tagGroupsByName
                    ),
                    (projectDisplayConfig["dataTypes"][
                      (isMedRow(key) || isMedGroupRow(key)
                        ? "medication"
                        : key) as keyof ProjectDisplayConfig["dataTypes"]
                    ]?.threshold as number) ?? 0,
                    entry.date,
                    patientOnboardedDate,
                    projectStart,
                    projectEnd,
                    isMedRow(key) || isMedGroupRow(key)
                      ? "medication"
                      : (key as keyof DataType),
                    projectDisplayConfig
                  )}
                  stroke="white"
                  onMouseEnter={(e) => handleMouseEnter(e, key, entry)}
                  onMouseLeave={() => setTooltip(null)}
                  // We only want to allow admins and clinicians to click on the cells to go to the patient record
                  onClick={
                    isAdmin ? () => handleCellClick(key, entry) : undefined
                  }
                  style={{ cursor: isAdmin ? "pointer" : "default" }}
                />
                {/* For the medication swimlane, we display a custom triangle if the user has custom medications */}
                {key === "medication" &&
                  isActive(
                    entry,
                    projectStart,
                    projectEnd,
                    patientStartEndDate
                  ) &&
                  hasCustomMedication(entry.medicationDisplayNames) && (
                    <polygon
                      points={`
                      ${
                        colIndex * cellSize + HORIZONTAL_OFFSET + cellSize - 7
                      },${rowIndex * (cellSize + padding) + 20}
                      ${colIndex * cellSize + HORIZONTAL_OFFSET + cellSize},${
                        rowIndex * (cellSize + padding) + 20
                      }
                      ${colIndex * cellSize + HORIZONTAL_OFFSET + cellSize},${
                        rowIndex * (cellSize + padding) + 27
                      }
                    `}
                      fill={COLORS.BLUEBERRY_300}
                    />
                  )}
                {isFirstOfMonth(entry.date) && (
                  <line
                    x1={colIndex * cellSize + HORIZONTAL_OFFSET}
                    y1={rowIndex * (cellSize + padding) + padding * 4}
                    x2={colIndex * cellSize + HORIZONTAL_OFFSET}
                    y2={
                      rowIndex * (cellSize + padding) + padding * 4 + cellSize
                    }
                    stroke="black"
                    strokeWidth={2}
                  />
                )}
                {isMonday(entry.date) && (
                  <line
                    x1={colIndex * cellSize + HORIZONTAL_OFFSET}
                    y1={rowIndex * (cellSize + padding) + padding * 4}
                    x2={colIndex * cellSize + HORIZONTAL_OFFSET}
                    y2={
                      rowIndex * (cellSize + padding) + padding * 4 + cellSize
                    }
                    stroke="gray"
                    strokeWidth={1}
                  />
                )}
              </g>
            ))}
          </React.Fragment>
        ))}
      </svg>
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y - 50,
            padding: "5px",
            border: "1px solid gray",
            backgroundColor: "white"
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.text }}
        />
      )}
      {legendTooltip && (
        <div
          onMouseEnter={() => handleLegendMouseEnter(legendTooltip.key)}
          onMouseLeave={handleLegendMouseLeave}
          style={{
            position: "absolute",
            left: 80,
            top:
              orderedDataKeys.indexOf(legendTooltip.key) *
                (cellSize + padding) +
              5,
            padding: "5px",
            border: "1px solid gray",
            backgroundColor: "white",
            zIndex: 1000,
            borderRadius: "4px",
            fontSize: "12px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}
        >
          {legendTooltip.text}
        </div>
      )}
    </div>
  );
};

export default DataAvailabilityHeatmapCalendarField;
